import type { ReactNode } from 'react';
import { cn } from './cn';

/* ------------------------------------------------------------------ dot -- */

export interface StatusDotProps {
  /** `live` is the only state that earns pink. */
  status?: 'live' | 'recent' | 'away';
  size?: number;
  className?: string;
}

const DOT: Record<NonNullable<StatusDotProps['status']>, string> = {
  live: 'bg-hottr-pink shadow-neon-soft animate-pulse-neon',
  recent: 'bg-white/45',
  away: 'bg-white/20',
};

/**
 * Presence dot. Live is pink — canon bans green "online" dots outright, and
 * that one rule does more for brand recognition than most of the palette.
 */
export const StatusDot = ({ status = 'live', size = 6, className }: StatusDotProps) => (
  <span
    aria-hidden
    style={{ width: size, height: size }}
    className={cn('inline-block shrink-0 rounded-full', DOT[status], className)}
  />
);

/* ----------------------------------------------------------------- chip -- */

export type ChipTone = 'neutral' | 'live' | 'selected' | 'value';

export interface StatusChipProps {
  children: ReactNode;
  tone?: ChipTone;
  /** Leading presence dot — only meaningful on `live`. */
  dot?: boolean;
  className?: string;
}

/**
 * Small metadata chip.
 *
 * Sharp 2px, not a pill — canon reserves full radius for avatars and dots.
 *
 * Tone governs pink discipline. `live` and `selected` may use magenta because
 * they signal state; `value` (rates, tiers) stays white so it cannot compete
 * with the primary action. `.men` currently pinks its prices AND its tags AND
 * its buttons, which is precisely why its booking CTA stops reading as primary.
 */
const TONE: Record<ChipTone, string> = {
  neutral: 'border border-white/[0.12] bg-white/[0.04] text-white/72',
  live: 'border border-hottr-pink/30 bg-hottr-pink/10 text-hottr-pink',
  selected: 'border border-hottr-pink bg-hottr-pink text-white',
  value: 'border border-white/[0.18] bg-transparent text-white',
};

export const StatusChip = ({ children, tone = 'neutral', dot, className }: StatusChipProps) => (
  <span
    className={cn(
      'inline-flex items-center gap-1.5 rounded-sm px-2 py-1',
      'font-display text-[10px] font-bold uppercase leading-none tracking-hottr-label',
      TONE[tone],
      className,
    )}
  >
    {dot && <StatusDot status={tone === 'live' ? 'live' : 'recent'} size={5} />}
    {children}
  </span>
);
